"use client";

import Image from "next/image";
import React, { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import {
    MagnifyingGlassIcon,
    InformationCircleIcon,
    ChartBarIcon,
    GlobeAltIcon,
    ClockIcon,
    ArrowPathIcon,
    ChevronRightIcon,
    CheckCircleIcon,
    XCircleIcon,
    QuestionMarkCircleIcon,
    ArrowTopRightOnSquareIcon,
    Cog6ToothIcon,
    LightBulbIcon,
    QuestionMarkCircleIcon as FAQIcon
} from "@heroicons/react/24/outline";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Filler,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { useRouter } from "next/navigation";
import { toast } from "react-hot-toast";
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import config from "@/config";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Filler,
    Legend
);

// Force dynamic rendering to avoid build-time errors with useSearchParams
export const dynamic = 'force-dynamic';
export const runtime = 'edge';

const SUFFIXES = ['.com', '.ai', '.org', '.net', '.im', '.info', '.app', '.io', '.xyz', '.co', '.run', '.me', '.pro', '.top'];

const REGISTRARS = [
    {
        name: "Namecheap",
        logo: "https://www.google.com/s2/favicons?domain=namecheap.com&sz=64",
        url: (domain: string) => `https://www.namecheap.com/domains/registration/results/?domain=${domain}`,
    },
    {
        name: "GoDaddy",
        logo: "https://www.google.com/s2/favicons?domain=godaddy.com&sz=64",
        url: (domain: string) => `https://www.godaddy.com/en-sg/domainsearch/find?domainToCheck=${domain}`,
    },
    {
        name: "Dynadot",
        logo: "https://www.google.com/s2/favicons?domain=dynadot.com&sz=64",
        url: (domain: string) => `https://www.dynadot.com/domain/search?domain=${domain}`,
    },
    {
        name: "Porkbun",
        logo: "https://www.google.com/s2/favicons?domain=porkbun.com&sz=64",
        url: (_: string) => `https://porkbun.com`,
    },
    {
        name: "Cloudflare",
        logo: "https://www.google.com/s2/favicons?domain=cloudflare.com&sz=64",
        url: (_: string) => `https://www.cloudflare.com/products/registrar/`,
    },
    {
        name: "Spaceship",
        logo: "https://www.google.com/s2/favicons?domain=spaceship.com&sz=64",
        url: (domain: string) => `https://www.spaceship.com/domain-search/?query=${domain}`,
    }
];

export default function DomainSearchPage({ params: { locale } }: { params: { locale: string } }) {
    const t = useTranslations("DomainInvestigator");
    const router = useRouter();
    const supabase = createClientComponentClient();
    const [keyword, setKeyword] = useState("");
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(false);
    const [domainList, setDomainList] = useState<any[]>([]);
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
    const [details, setDetails] = useState<any>(null);
    const [error, setError] = useState("");

    // Custom Suffixes State
    const [activeSuffixes, setActiveSuffixes] = useState<string[]>(SUFFIXES);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tempSuffixes, setTempSuffixes] = useState("");

    const FaviconImage = ({ domain, size = 16, className = "" }: { domain: string, size?: number, className?: string }) => {
        const [error, setError] = useState(false);
        const src = `https://www.google.com/s2/favicons?domain=${domain}&sz=${size * 2}`;

        if (error) {
            return <GlobeAltIcon className={`${className}`} style={{ width: size, height: size }} />;
        }

        return (
            <img
                src={src}
                alt=""
                width={size}
                height={size}
                className={`${className}`}
                onError={() => setError(true)}
            />
        );
    };

    useEffect(() => {
        const saved = localStorage.getItem("search_suffixes");
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setActiveSuffixes(parsed);
                }
            } catch (e) {
                console.error("Failed to load suffixes", e);
            }
        }
    }, []);

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();

        // Check if user is logged in
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            toast.error(t("pleaseLogin"));
            router.push(config.auth.loginUrl);
            return;
        }

        if (!keyword) return;

        // Clean keyword
        const cleanKeyword = keyword.replace(/\s/g, '').toLowerCase().split('.')[0];
        if (!cleanKeyword) return;

        setChecking(true);
        setError("");
        setDomainList([]);
        setSelectedDomain(null);
        setDetails(null);

        try {
            const domainsToCheck = activeSuffixes.map(s => `${cleanKeyword}${s.startsWith('.') ? s : '.' + s}`).join(',');
            const res = await fetch(`/api/domain?type=check&domain=${domainsToCheck}`);
            const data = await res.json();

            if (data.error) throw new Error(data.error);

            const list = data.data?.domains || [];
            setDomainList(list);

            if (list.length > 0) {
                handleSelectDomain(list[0].domain);
            }
        } catch (err) {
            setError(t("error"));
        } finally {
            setChecking(false);
        }
    };

    const handleSelectDomain = async (domain: string) => {
        setSelectedDomain(domain);
        setLoading(true);
        setDetails(null);

        try {
            const [whoisRes, trafficRes] = await Promise.all([
                fetch(`/api/domain?type=whois&domain=${domain}`).then(res => res.json()),
                fetch(`/api/domain?type=traffic&domain=${domain}`).then(res => res.json()),
            ]);

            setDetails({
                whois: whoisRes,
                traffic: trafficRes,
            });
        } catch (err) {
            console.error("Failed to fetch details", err);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return "N/A";
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'available': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
            case 'registered': return <XCircleIcon className="w-4 h-4 text-gray-400" />;
            default: return <QuestionMarkCircleIcon className="w-4 h-4 text-yellow-500" />;
        }
    };

    const trafficData = details?.traffic?.data?.traffic ? {
        labels: Object.keys(details.traffic.data.traffic).reverse(),
        datasets: [
            {
                fill: true,
                label: 'Traffic',
                data: Object.values(details.traffic.data.traffic).reverse(),
                borderColor: 'rgb(59, 130, 246)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                tension: 0.4,
            },
        ],
    } : null;

    const openSettings = () => {
        setTempSuffixes(activeSuffixes.join('\n'));
        setIsModalOpen(true);
    };

    const saveSettings = () => {
        const list = tempSuffixes.split('\n')
            .map(s => s.trim())
            .filter(s => s.length > 0)
            .slice(0, 20);

        setActiveSuffixes(list);
        localStorage.setItem("search_suffixes", JSON.stringify(list));
        setIsModalOpen(false);
    };

    const isAvailable = domainList.find(d => d.domain === selectedDomain)?.status === 'available';

    return (
        <>
            <Header locale={locale} />
            <div className="min-h-screen bg-[#f8fafc] pb-20 font-sans text-slate-900">
                <header className="border-b border-slate-200 bg-white px-4 py-10">
                    <div className="mx-auto max-w-7xl">
                        <h1 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">{t("title")}</h1>
                        <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600 sm:text-lg">{t("description")}</p>
                    </div>
                </header>
                {/* Search Header */}
                <div className="bg-white border-b sticky top-0 z-20 px-4 py-4">
                    <div className="max-w-7xl mx-auto">
                        <form onSubmit={handleSearch} className="relative group">
                            <input
                                type="text"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                placeholder={t("searchPlaceholder")}
                                className="w-full bg-slate-50 border-none rounded-xl py-4 pl-14 pr-32 text-lg focus:ring-2 focus:ring-blue-500 transition-all shadow-inner"
                            />
                            <MagnifyingGlassIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={openSettings}
                                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-blue-600 group/cog"
                                    title="Search Settings"
                                >
                                    <Cog6ToothIcon className="w-5 h-5 group-hover/cog:rotate-90 transition-transform duration-500" />
                                </button>
                                <span className="text-xs text-slate-400 hidden sm:block">{activeSuffixes.length} suffixes</span>
                                <button
                                    type="submit"
                                    disabled={checking}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-bold transition-all disabled:bg-slate-300 flex items-center shadow-lg shadow-blue-200"
                                >
                                    {checking && <ArrowPathIcon className="w-4 h-4 animate-spin mr-2" />}
                                    {t("searchButton")}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 mt-6">
                    <div className="flex flex-col lg:flex-row gap-6">

                        {/* Sidebar - List of Domains */}
                        <div className="w-full lg:w-80 flex-shrink-0">
                            <div className="bg-white rounded-2xl shadow-sm border overflow-hidden p-2 space-y-1">
                                {domainList.length > 0 ? (
                                    domainList.map((item) => (
                                        <button
                                            key={item.domain}
                                            onClick={() => handleSelectDomain(item.domain)}
                                            className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${selectedDomain === item.domain
                                                ? 'bg-blue-50 border-blue-100 ring-1 ring-blue-100'
                                                : 'hover:bg-slate-50'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                {item.status === 'registered' ? (
                                                    <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                                                        <FaviconImage domain={item.domain} size={16} className="rounded-sm" />
                                                    </div>
                                                ) : (
                                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.status === 'available' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                                                )}
                                                <span className={`font-medium truncate ${selectedDomain === item.domain ? 'text-blue-700' : 'text-slate-700'}`}>
                                                    {item.domain}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {item.registered && (
                                                    <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-mono">
                                                        {new Date(item.registered).getFullYear()}
                                                    </span>
                                                )}
                                                <ChevronRightIcon className={`w-4 h-4 transition-transform ${selectedDomain === item.domain ? 'translate-x-1 text-blue-500' : 'text-slate-300'}`} />
                                            </div>
                                        </button>
                                    ))
                                ) : checking ? (
                                    Array.from({ length: 10 }).map((_, i) => (
                                        <div key={i} className="h-12 bg-slate-100 animate-pulse rounded-xl mb-1" />
                                    ))
                                ) : (
                                    <div className="p-8 text-center text-slate-400">
                                        <GlobeAltIcon className="w-10 h-10 mx-auto mb-2 opacity-20" />
                                        <p className="text-sm">Start searching</p>
                                    </div>
                                )}
                            </div>

                            {/* Status Legend */}
                            <div className="mt-4 flex items-center justify-center lg:justify-start gap-4 text-[10px] uppercase font-bold tracking-wider text-slate-400">
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-green-500" /> {t("available")}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-yellow-500" /> {t("uncertain")}
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 rounded-full bg-slate-300" /> {t("unavailable")}
                                </div>
                            </div>
                        </div>

                        {/* Main Detail View */}
                        <div className="flex-1 min-w-0">
                            {selectedDomain ? (
                                <div className="space-y-6">

                                    {/* Domain Hero Card */}
                                    <div className="bg-white rounded-2xl shadow-sm border p-8 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-8">
                                            {isAvailable ? (
                                                <a
                                                    href={REGISTRARS[0].url(selectedDomain)}
                                                    target="_blank"
                                                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1.5 font-bold text-sm px-6 py-2.5 rounded-full transition-all shadow-lg shadow-blue-200"
                                                >
                                                    {t("registerNow")} <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                                </a>
                                            ) : (
                                                <a
                                                    href={`https://${selectedDomain}`}
                                                    target="_blank"
                                                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1.5 font-bold text-sm bg-blue-50 px-4 py-2 rounded-full transition-colors"
                                                >
                                                    {t("visitWebsite")} <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                                </a>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="w-12 h-12 bg-white border rounded-xl flex items-center justify-center overflow-hidden">
                                                <FaviconImage domain={selectedDomain} size={32} />
                                            </div>
                                            <h2 className="text-3xl font-black uppercase tracking-tight">{selectedDomain}</h2>
                                        </div>


                                    </div>

                                    {loading ? (
                                        <div className="space-y-6">
                                            <div className="bg-white rounded-2xl border h-64 animate-pulse" />
                                            <div className="bg-white rounded-2xl border h-64 animate-pulse" />
                                        </div>
                                    ) : (
                                        <>
                                            {/* Domain Meta Info */}
                                            {!isAvailable && (
                                                <>
                                                    <div className="bg-white rounded-2xl shadow-sm border p-8">
                                                        <div className="flex items-center gap-3 mb-8">
                                                            <InformationCircleIcon className="w-5 h-5 text-blue-500" />
                                                            <h3 className="font-bold text-slate-700 uppercase tracking-widest text-sm">{t("general")}</h3>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-8 gap-x-12">
                                                            <DetailItem label={t("registered")} value={formatDate(details?.whois?.parsed?.registered)} />
                                                            <DetailItem label={t("expires")} value={formatDate(details?.whois?.parsed?.expires)} />
                                                            <DetailItem label="Updated" value={formatDate(details?.whois?.parsed?.d_updated)} />
                                                            <DetailItem label={t("registrar")} value={details?.whois?.parsed?.registrar} />
                                                            <DetailItem label={t("status")} value={details?.whois?.parsed?.status} />
                                                            <DetailItem label={t("nameservers")} value={details?.whois?.parsed?.nameservers?.split(',').join('\n')} isPre />
                                                        </div>
                                                    </div>

                                                    {/* Traffic Section */}
                                                    <div className="bg-white rounded-2xl shadow-sm border p-8">
                                                        <div className="flex items-center gap-3 mb-8">
                                                            <ChartBarIcon className="w-5 h-5 text-purple-500" />
                                                            <h3 className="font-bold text-slate-700 uppercase tracking-widest text-sm">{t("traffic")} (5 months)</h3>
                                                        </div>
                                                        <div className="h-64">
                                                            {trafficData ? (
                                                                <Line
                                                                    data={trafficData}
                                                                    options={{
                                                                        responsive: true,
                                                                        maintainAspectRatio: false,
                                                                        plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false } },
                                                                        scales: { x: { grid: { display: false } }, y: { beginAtZero: true, grid: { tickBorderDash: [2, 2] } } }
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="h-full flex items-center justify-center text-slate-300 italic">No data available</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </>
                                            )}

                                            {/* Registrar List for Available Domains */}
                                            {isAvailable && (
                                                <div className="bg-white rounded-2xl shadow-sm border p-8">
                                                    <div className="flex items-center gap-3 mb-8">
                                                        <GlobeAltIcon className="w-5 h-5 text-green-500" />
                                                        <h3 className="font-bold text-slate-700 uppercase tracking-widest text-sm">{t("registrarList")}</h3>
                                                    </div>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                                        {REGISTRARS.map((registrar) => (
                                                            <a
                                                                key={registrar.name}
                                                                href={registrar.url(selectedDomain)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-blue-200 hover:bg-blue-50 transition-all group"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center overflow-hidden">
                                                                        <img src={registrar.logo} alt={registrar.name} className="w-6 h-6 object-contain" />
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-sm font-bold text-slate-700 group-hover:text-blue-700">{registrar.name}</p>
                                                                        <p className="text-[10px] text-slate-400 uppercase tracking-widest">{t("checkOn")}</p>
                                                                    </div>
                                                                </div>
                                                                <ArrowTopRightOnSquareIcon className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}

                                </div>
                            ) : (
                                <div className="bg-white rounded-3xl border border-dashed border-slate-200 h-[600px] flex flex-col items-center justify-center text-slate-300">
                                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-6">
                                        <MagnifyingGlassIcon className="w-10 h-10" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-400">Select a domain to investigate</h3>
                                    <p className="mt-2">Bulk results will appear on the left</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 mt-16 space-y-16">
                {/* Usage Instructions */}
                <section>
                    <div className="flex items-center gap-3 mb-8">
                        <LightBulbIcon className="w-8 h-8 text-yellow-500" />
                        <h2 className="text-2xl font-black uppercase tracking-tight">{t("howToUseTitle")}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {(t.raw("howToUseSteps") as any[]).map((step, index) => (
                            <div key={index} className="bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center font-black mb-4">
                                    {index + 1}
                                </div>
                                <h4 className="font-bold text-slate-900 mb-2">{step.title}</h4>
                                <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* FAQ Section */}
                <section>
                    <div className="flex items-center gap-2 mb-8">
                        <FAQIcon className="w-8 h-8 text-blue-500" />
                        <h2 className="text-2xl font-black uppercase tracking-tight">{t("faqTitle")}</h2>
                    </div>
                    <div className="grid gap-4">
                        {(t.raw("faq") as any[]).map((item, index) => (
                            <details key={index} className="group bg-white rounded-2xl border shadow-sm overflow-hidden">
                                <summary className="flex items-center justify-between p-6 cursor-pointer hover:bg-slate-50 transition-colors list-none">
                                    <h4 className="font-bold text-slate-800 pr-4">{item.question}</h4>
                                    <ChevronRightIcon className="w-5 h-5 text-slate-400 group-open:rotate-90 transition-transform" />
                                </summary>
                                <div className="px-6 pb-6 text-slate-600 border-t pt-4">
                                    <p className="leading-relaxed">{item.answer}</p>
                                </div>
                            </details>
                        ))}
                    </div>
                </section>
            </div>

            <Footer />

            {/* Structured Data for FAQ */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "FAQPage",
                        "mainEntity": (t.raw("faq") as any[]).map(item => ({
                            "@type": "Question",
                            "name": item.question,
                            "acceptedAnswer": {
                                "@type": "Answer",
                                "text": item.answer
                            }
                        }))
                    })
                }}
            />

            {/* Settings Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-8">
                            <h3 className="text-xl font-bold mb-6 text-center">Search Settings</h3>
                            <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100 mb-4">
                                <textarea
                                    value={tempSuffixes}
                                    onChange={(e) => setTempSuffixes(e.target.value)}
                                    className="w-full h-64 bg-transparent border-none focus:ring-0 text-slate-700 font-mono resize-none"
                                    placeholder=".com&#10;.ai&#10;.net"
                                />
                            </div>
                            <p className="text-xs text-slate-400 text-center mb-8">
                                One TLD per line, with a maximum of 20 TLDs.
                            </p>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 py-3 px-6 rounded-xl font-bold text-slate-500 hover:bg-slate-50 transition-colors border border-slate-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveSettings}
                                    className="flex-1 py-3 px-6 rounded-xl font-bold text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function DetailItem({ label, value, isPre = false }: { label: string; value: string; isPre?: boolean }) {
    return (
        <div>
            <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest mb-1.5">{label}</p>
            {isPre ? (
                <pre className="text-xs font-mono text-slate-700 whitespace-pre-wrap leading-relaxed">{value || "N/A"}</pre>
            ) : (
                <p className="text-sm font-bold text-slate-700">{value || "N/A"}</p>
            )}
        </div>
    );
}
